import { useParams } from 'react-router-dom'
import { useGlobalErrorHandler, useMutation } from '@src/hooks'
import { SettingService } from '@src/domain/services'
import { Category } from '@src/domain/repositories/setting.repo'
import { useDispatch, useSelector, settingActions } from '@src/store'
import { useEffect, useState } from 'react'
import { ModalResolveType, useModal } from '@src/lib/modal-manager'
import { useDialog } from '@src/components/dialog'
import { editModalMap } from './config'
import { useSpinner } from '@src/components/spinner'

export function SettingViewModel() {
  const params = useParams()
  const category = params.category as Category
  const [currentPage, setPage] = useState(1)
  const [keyword, setKeyword] = useState('')
  const dispatch = useDispatch()
  const { showModal } = useModal()
  const { spinner } = useSpinner()
  const { dialog } = useDialog()
  const { globalErrorHandler } = useGlobalErrorHandler()

  const { mutate: getItemByCategroy } = useMutation(SettingService.getItemByCategroy)
  const { mutate: addItemByCategory } = useMutation(SettingService.addItemByCategory)
  const { mutate: updateItemByCategroy } = useMutation(SettingService.updateItemByCategroy)
  const { mutate: deleteItemByCategroy } = useMutation(SettingService.deleteItemByCategroy)
  const { mutate: getItemListByCategory } = useMutation(SettingService.getItemListByCategory)

  const { itemList } = useSelector(state => state.setting)

  const fetchDefaultList = async () => {
    const itemListDto = await spinner(getItemListByCategory({ keyword: '', page: 1, category }))

    dispatch(settingActions.setItemList(itemListDto))
    setPage(1)
    setKeyword('')
  }
  
  useEffect(() => {
    fetchDefaultList()

    return () => {
      dispatch(
        settingActions.clearItemList()
      )
      setPage(1)
      setKeyword('')
    }
  }, [category])

  const getItemListByPageNumber = async (pageNumber: number, keyword: string) => {
    const itemListDto = await getItemListByCategory({ keyword, page: pageNumber, category })

    dispatch(
      settingActions.setItemList(itemListDto)
    )
  }

  const handleKeywordOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(e.target.value)
  }

  const handleSearchClick = async () => {
    getItemListByPageNumber(1, keyword)
    setPage(1)
  }

  const handleNextOnClick = async () => {
    if (currentPage === itemList?.totalPages) return
    await getItemListByPageNumber(currentPage + 1, keyword)
    setPage(currentPage + 1)
  }
  
  const handlePrevOnClick = async () => {
    if (currentPage === 1) return
    await getItemListByPageNumber(currentPage - 1, keyword)
    setPage(currentPage - 1)
  }

  const handleMoveToOnClick = async (pageNumber: number) => {
    await getItemListByPageNumber(pageNumber, keyword)
    setPage(pageNumber)
  }

  const handleEditBtnOnClick = async (rowData: any) => {
    try {
      const { uuid } = rowData
      const itemDto = await spinner(getItemByCategroy({ category, uuid })) as any

      const result = await showModal(editModalMap[category], { dtoUuid: uuid, itemDto })

      if(result.type !== ModalResolveType.OK) return

      const data = result.data as SettingService.GetItemByCategoryRespBo

      await spinner(updateItemByCategroy({ category, uuid, data }))

      await fetchDefaultList()
    } catch (error) {
      globalErrorHandler(error)
    }
  }

  const handleAddBtnOnClick = async () => {
    try {
      const result = await showModal(editModalMap[category])

      if(result.type !== ModalResolveType.OK) return

      const data = result.data as SettingService.GetItemByCategoryRespBo

      await spinner(addItemByCategory({ category, data }))

      await fetchDefaultList()
    } catch (error) {
      globalErrorHandler(error)
    }
  }

  const handleDelBtnOnClick = async (rowData: any) => {
    const { uuid } = rowData
    const result = await dialog.confirm({
      title: '刪除確認',
      message: '是否要刪除這筆資料'
    })

    if (result.type !== ModalResolveType.OK) return

    try {
      await spinner(deleteItemByCategroy({ category, uuid }))
      await fetchDefaultList()
    } catch (error) {
      globalErrorHandler(error)
    }
  }

  return {
    category,
    currentPage,
    keyword,
    setKeyword,
    itemList,
    handleKeywordOnChange,
    handleSearchClick,
    handleNextOnClick,
    handlePrevOnClick,
    handleMoveToOnClick,
    handleAddBtnOnClick,
    handleEditBtnOnClick,
    handleDelBtnOnClick
  }
}

export type ISettingViewModel = ReturnType<typeof SettingViewModel>
